<template>
  <Hero :title="data?.header.title" :subtitle="data?.header.subtitle" :narrow="true" :images="data?.header.images"/>
  <main class="main maxw--waist block">
    <article class="rendered text--default" v-html="$md.render(data?.content || '')"></article>
    <form @submit.prevent="submit" class="form">
      <fieldset name="general" class="block form__fieldset">
        <h3 class="text--default">Algemene gegevens</h3>
        <div class="form__row">
          <FormTextField label="Naam" name="firstname" class="field" v-model="v$.name.$model" :validator="v$.name"/>
        </div>
        <div class="form__row">
          <FormTextField label="E-mail" name="email" type="email" class="field" v-model="v$.email.$model" :validator="v$.email"/>
        </div>
        <div class="form__row">
          <FormTextField label="Telefoonnummer" name="phone" class="field" v-model="v$.phone.$model" :validator="v$.phone"/>
        </div>
        <div class="form__row">
          <FormDateField label="Geboortedatum" name="birthdate" class="field" v-model="v$.birthdate.$model" :validator="v$.birthdate"/>
        </div>
        <div class="form__row">
          <FormTextField label="School" name="school" class="field" v-model="v$.school.$model" :validator="v$.school"/>
        </div>
        <div class="form__row">
          <FormTextField label="Opleiding" name="study" class="field" v-model="v$.study.$model" :validator="v$.study"/>
        </div>
        <div class="form__row">
          <FormNumberField label="Schooljaar" name="studyYear" class="field" v-model="v$.studyYear.$model" :validator="v$.studyYear"/>
        </div>
        <div class="form__row">
          <FormTextField label="Hoe heb je van ons gehoord?" name="source" class="field" v-model="formData.source"/>
        </div>
        <div class="form__row">
          <FormDropdown 
            label="Ken je al mensen binnen de vereniging?" 
            name="knowsPeople" 
            class="field" 
            v-model="formData.knowsPeople"
            :options="[{ label: 'Ja', value: true }, { label: 'Nee', value: false }]"
          />
        </div>
      </fieldset>
      <fieldset name="events" class="block form__fieldset">
        <h3 class="text--default">Open feesten</h3>
        <div class="form__row">
          <div>
            <label class="field__label static">Bij welke open feesten heb je eventueel een slaapplek nodig?</label>
            <div 
              class="field--inline" 
              v-for="(event, i) in data?.introductionForm?.eventsForQuestion"
              :key="i"
            >
              <FormCheckbox
                class="field--inside"
                v-model="formData.events[i]"
              >
                {{event.dateText}} - {{event.shortDescription}}
              </FormCheckbox>
              <nuxt-link :href="event.link" target="_blank" v-if="event.link">
                <IconsLink/>
              </nuxt-link>
            </div>
          </div>
        </div>
      </fieldset>
      <fieldset name="submit" class="form__fieldset mt-2">
        <NuxtTurnstile v-model="token" v-if="!isDisabled"/>
        <div class="form__row">
          <button class="btn btn--flex" type="submit" :disabled="isLoading || !v$.$dirty">
            Verzenden
            <Spinner v-if="isLoading"/>
          </button>
        </div>
        <div class="form__row" v-if="!neutral">
          <span class="mt-1 field__status" :class="feedbackClass">{{ feedbackMessage }}</span>
        </div>
      </fieldset>
    </form>
  </main>
</template>

<script lang="ts" setup>
import useVuelidate from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';
import { format } from 'date-fns';
import IntroductionPage from '~/typings/pages/introduction_page';
import IntroductionWeekResponse, { IntroductionWeekResponseEvent } from '~/typings/items/introductionWeekResponse';

const { findOne, create } = useStrapi();
const { token, isDisabled, verify } = useTurnstile();
const { isLoading, startLoading, stopLoading } = useLoading();
const { feedbackMessage, resetStatus, setStatus, handleSuccess, neutral, feedbackClass } = useSubmitFeedback(
  'Bedankt voor je aanmelding! Je ontvangt zo snel mogelijk een bevestiging per e-mail.',
  'Er is iets fout gegaan. Probeer het later opnieuw'
);

const { data } = await useAsyncData(
  'introductionpage',
  () => findOne<IntroductionPage>('introduction-week-page', 
    { 
      populate: ['header', 'header.images', 'introductionForm', 'introductionForm.eventsForQuestion'] 
    }
  ),
  {
    transform: flattenStrapiObject
  }
);

useHead({
  title: data.value?.header.title || 'Introductieperiode Virgo'
});

const baseData: IntroductionWeekResponse = {
  name: '',
  email: '',
  phone: '',
  birthdate: '',
  school: '',
  study: '',
  studyYear: null,
  knowsPeople: null,
  source: '',
  events: [] as boolean[],
}

const formData = reactive({...baseData});

async function submit(e: Event) {
  resetStatus();

  if (!(await v$.value.$validate()) || isLoading.value) return;

  startLoading();

  try {
    if (!(await verify())) {
      setStatus(false)
      return;
    }

    // We want to format the events array
    const postData: IntroductionWeekResponse = { ...formData };
    const eventValues = [...formData.events] as boolean[];

    // @ts-ignore
    postData.events = data.value?.introductionForm?.eventsForQuestion.map<IntroductionWeekResponseEvent>((event, i: number) => {
      let checkedValue = false;
      
      if (i >= 0 && i < eventValues.length) {
        checkedValue = eventValues[i] || false;
      }

      return {
        name: `${event?.dateText} - ${event?.shortDescription}`,
        checked: checkedValue
      } as IntroductionWeekResponseEvent;
    });

    // format birth date to simple format
    postData.birthdate = format(new Date(postData.birthdate), 'yyyy-MM-dd');

    await create<IntroductionWeekResponse>('introduction-period-responses', postData);

    handleSuccess();

    v$.value.$reset();
    Object.assign(formData, baseData);
    formData.events = []; // Specificaly reset the events array

  } catch (e) {
    setStatus(false);
    // @ts-ignore
    useBugsnag().notify(e);
  } finally {
    stopLoading();
  }

}

const rules = computed(() => ({
  name: {
    required: helpers.withMessage('Dit veld is verplicht', required)
  },
  email: {
    required: helpers.withMessage('Dit veld is verplicht', required),
    email: helpers.withMessage('Dit is geen geldig e-mailadres', email)
  },
  phone: {
    required: helpers.withMessage('Dit veld is verplicht', required),
  },
  birthdate: {
    required: helpers.withMessage('Dit veld is verplicht', required),
  },
  school: {
    required: helpers.withMessage('Dit veld is verplicht', required),
  },
  study: {
    required: helpers.withMessage('Dit veld is verplicht', required),
  },
  studyYear: {
    required: helpers.withMessage('Dit veld is verplicht', required),
  }
}));

const v$ = useVuelidate(rules, formData);
</script>
